/**
 * Advanced Economics fast-path mirror engine.
 * Ported verbatim from sauron/frontend/src/domain/advanced_economics/engine.ts.
 * Mirrors backend/app/domain/advanced_economics/engine.py exactly.
 * Supports 1–3 scenarios, TACOS presets, pairwise deltas, estimated_total_profit.
 */
import type {
    ScenarioInput,
    ScenarioOutputMetrics,
    ScenarioDelta,
    ScenarioResult,
    TemplateId,
} from './types';
import { TACOS_PRESETS, TACOS_ZONES } from './types';

const SLOT_LABELS = ['Scenario 1', 'Scenario 2', 'Scenario 3'];

export function resolveTacos(
    templateId: TemplateId,
    tacosPercent: number | null
): { effective: number; isCustom: boolean } {
    const preset = TACOS_PRESETS[templateId] ?? 12;
    if (tacosPercent !== null) {
        return { effective: tacosPercent, isCustom: tacosPercent !== preset };
    }
    return { effective: preset, isCustom: false };
}

export function resolveTacosZone(templateId: TemplateId, tacos: number): string {
    const zones = TACOS_ZONES[templateId] ?? TACOS_ZONES.steady_state;
    for (const zone of zones) {
        if (tacos >= zone.lo && tacos < zone.hi) return zone.label;
    }
    return 'Extreme';
}

function computeScenarioMetrics(s: ScenarioInput): ScenarioOutputMetrics {
    const sellPrice = s.sellPrice ?? 0;
    const cogs = s.cogs ?? 0;
    const { effective: tacosEffective, isCustom: tacosIsCustom } = resolveTacos(
        s.templateId,
        s.tacosPercent
    );

    const tacosRate = s.advertisingEnabled ? tacosEffective / 100 : 0;
    const refundAdminFee = Math.min(5, 0.2 * sellPrice * s.referralFeeRate);
    const nonrecoverableAdCost = sellPrice * tacosRate;
    const returnsCost =
        s.returnsRate *
        (refundAdminFee + s.fbaFixedFee + nonrecoverableAdCost + (1 - s.returnsRecoveryRate) * cogs);
    const amazonFees = sellPrice * s.referralFeeRate + s.fbaFixedFee;
    const adCost = sellPrice * tacosRate;
    const otherExpensesCost =
        s.otherExpenses != null
            ? s.otherExpensesMode === 'percent'
                ? (sellPrice * s.otherExpenses) / 100
                : s.otherExpenses
            : 0;
    const baseProfit = sellPrice - amazonFees - returnsCost - cogs;
    const profitPerUnit = baseProfit - adCost - otherExpensesCost;
    const netMarginPercent = sellPrice > 0 ? (profitPerUnit / sellPrice) * 100 : 0;
    const roiOnCogsPercent = cogs > 0 ? (profitPerUnit / cogs) * 100 : 0;

    const variableRate =
        s.referralFeeRate + s.returnsRate * (0.2 * s.referralFeeRate + tacosRate) + tacosRate;
    const fixedCosts =
        cogs * (1 + s.returnsRate * (1 - s.returnsRecoveryRate)) + s.fbaFixedFee * (1 + s.returnsRate);
    const breakEvenSafeFallback = variableRate >= 1;
    const displayedBreakEvenPrice = breakEvenSafeFallback ? null : fixedCosts / (1 - variableRate);

    const baseBeAcos =
        sellPrice > 0
            ? Math.max(((sellPrice - amazonFees - returnsCost - cogs) / sellPrice) * 100, 0)
            : 0;
    const breakEvenAcosPercent = s.advertisingEnabled
        ? Math.max(baseBeAcos - tacosEffective, 0)
        : baseBeAcos;

    const estimatedTotalProfit =
        s.unitsSoldOnThisScenario != null
            ? Math.round(s.unitsSoldOnThisScenario * profitPerUnit * 100) / 100
            : null;

    return {
        advertisingCostPerUnit: r4(adCost),
        returnsCostPerUnit: r4(returnsCost),
        otherExpensesCostPerUnit: r4(otherExpensesCost),
        amazonFeesAtCurrentPrice: r4(amazonFees),
        profitPerUnit: r4(profitPerUnit),
        netMarginPercent: r4(netMarginPercent),
        roiOnCogsPercent: r4(roiOnCogsPercent),
        displayedBreakEvenPrice: displayedBreakEvenPrice != null ? r2(displayedBreakEvenPrice) : null,
        breakEvenAcosPercent: r4(breakEvenAcosPercent),
        breakEvenSafeFallback,
        estimatedTotalProfit,
        tacosEffectivePercent: tacosEffective,
        tacosZoneId: resolveTacosZone(s.templateId, tacosEffective),
        tacosIsCustom,
    };
}

function computeDelta(
    left: ScenarioOutputMetrics,
    right: ScenarioOutputMetrics
): ScenarioDelta {
    return {
        profitPerUnitDelta: r4(right.profitPerUnit - left.profitPerUnit),
        netMarginDelta: r4(right.netMarginPercent - left.netMarginPercent),
        roiDelta: r4(right.roiOnCogsPercent - left.roiOnCogsPercent),
        advertisingCostDelta: r4(right.advertisingCostPerUnit - left.advertisingCostPerUnit),
    };
}

export function computeScenarios(scenarios: ScenarioInput[]): ScenarioResult[] {
    const results: ScenarioResult[] = [];
    let prevMetrics: ScenarioOutputMetrics | null = null;

    scenarios.forEach((s, i) => {
        const metrics = computeScenarioMetrics(s);
        const deltaVsLeft =
            prevMetrics !== null && s.compareAgainstLeft ? computeDelta(prevMetrics, metrics) : null;
        results.push({
            slotId: s.slotId,
            templateId: s.templateId,
            label: SLOT_LABELS[i] ?? `Scenario ${i + 1}`,
            metrics,
            deltaVsLeft,
        });
        prevMetrics = metrics;
    });

    return results;
}

export function addScenario(
    current: ScenarioInput[],
    templateId: TemplateId = 'stabilizing'
): { scenarios: ScenarioInput[]; addedSlotId: string } {
    if (current.length >= 3) return { scenarios: current, addedSlotId: '' };
    const slotIds = ['s1', 's2', 's3'];
    const nextIdx = current.length;
    const slotId = slotIds[nextIdx];
    const last = current[current.length - 1];
    const newScenario: ScenarioInput = {
        slotId,
        templateId,
        tacosPercent: null,
        tacosCustom: false,
        sellPrice: last?.sellPrice ?? 0,
        referralFeeRate: last?.referralFeeRate ?? 0.15,
        fbaFixedFee: last?.fbaFixedFee ?? 3.22,
        returnsRate: last?.returnsRate ?? 0.04,
        returnsRecoveryRate: last?.returnsRecoveryRate ?? 0.4,
        cogs: last?.cogs ?? null,
        otherExpenses: last?.otherExpenses ?? null,
        otherExpensesMode: last?.otherExpensesMode ?? 'per_unit',
        advertisingEnabled: last?.advertisingEnabled ?? true,
        unitsSoldOnThisScenario: null,
        compareAgainstLeft: true,
    };
    return { scenarios: [...current, newScenario], addedSlotId: slotId };
}

export function closeScenario(
    current: ScenarioInput[],
    closeSlotId: string
): { scenarios: ScenarioInput[]; results: ScenarioResult[] } {
    const remaining = current.filter(s => s.slotId !== closeSlotId);
    const slotIds = ['s1', 's2', 's3'];
    const reindexed = remaining.map((s, i) => ({
        ...s,
        slotId: slotIds[i],
        compareAgainstLeft: i > 0,
    }));
    return { scenarios: reindexed, results: computeScenarios(reindexed) };
}

// ── Helpers ─────────────────────────────────────────────────────────────────
const r4 = (n: number) => Math.round(n * 10000) / 10000;
const r2 = (n: number) => Math.round(n * 100) / 100;
